import { useEffect, useState } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, fmtDate, CLEAN_STATUS } from "../helpers";
import ImageUpload from "../components/ImageUpload";
import { Plus, Pencil, Trash2, Clock, User, Sparkles } from "lucide-react";

const today = new Date().toISOString().slice(0, 10);
const DEFAULT_CHECKLIST = ["Kit benvenuto", "Pulizia cucina", "Controllo bagno", "Controllo camera", "Controllo soggiorno", "Rifornimento prodotti", "Raccolta rifiuti", "Foto finali obbligatorie"];
const EXTRA_SERVICE_OPTIONS = ["Pulizia durante il soggiorno su richiesta", "Cambio lenzuola extra", "Cambio asciugamani extra", "Kit cortesia extra", "Pulizia cucina extra", "Late check-out con pulizia urgente"];
const empty = { apartment_id: "", date: today, time: "10:00", staff: "", status: "da_fare", checklist: [], extra_services: [], photo_before: "", photo_after: "", notes: "" };

import { Field } from "../components/FormField";

export default function Cleanings() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [apts, setApts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [newTask, setNewTask] = useState("");
  const [tab, setTab] = useState("active");

  const load = () => api.get("/cleanings").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/apartments/lookup").then((r) => setApts(r.data)); }, []);

  const aptName = (id) => apts.find((a) => a.id === id)?.name || "—";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm({ ...empty, apartment_id: apts[0]?.id || "", checklist: DEFAULT_CHECKLIST.map((l) => ({ label: l, done: false })), extra_services: [] }); setOpen(true); };
  const openEdit = (it) => { setForm({ ...it, checklist: it.checklist || [], extra_services: it.extra_services || [] }); setOpen(true); };

  const shown = items.filter((c) => tab === "active" ? c.status !== "completata" : c.status === "completata");

  const toggleTask = (i) => set("checklist", form.checklist.map((c, idx) => idx === i ? { ...c, done: !c.done } : c));
  const addTask = () => { if (!newTask.trim()) return; set("checklist", [...form.checklist, { label: newTask, done: false }]); setNewTask(""); };
  const removeTask = (i) => set("checklist", form.checklist.filter((_, idx) => idx !== i));

  const addExtra = (label) => set("extra_services", [...(form.extra_services || []), { id: Date.now().toString(), label, price: 0, paid_status: "da_pagare", payment_provider: "", payment_link: "", booking_ref: "", notes: "" }]);
  const updateExtra = (i, k, v) => set("extra_services", form.extra_services.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const removeExtra = (i) => set("extra_services", form.extra_services.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.apartment_id) { toast.error(t("selectApartment")); return; }
    try {
      if (form.id) await api.put(`/cleanings/${form.id}`, form);
      else await api.post("/cleanings", form);
      toast.success(t("saved")); setOpen(false); load();
    } catch { toast.error("Error"); }
  };
  const del = async (id) => { if (!window.confirm(t("confirm"))) return; await api.delete(`/cleanings/${id}`); toast.success(t("deleted")); load(); };

  return (
    <div className="space-y-6 fade-in" data-testid="cleanings-page">
      <div className="flex items-center justify-between">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
          <button data-testid="cleanings-tab-active" onClick={() => setTab("active")}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === "active" ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-100"}`}>
            {t("activeCleanings")}
          </button>
          <button data-testid="cleanings-tab-history" onClick={() => setTab("history")}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === "history" ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-100"}`}>
            {t("cleaningHistory")}
          </button>
        </div>
        <Button data-testid="add-cleaning-btn" onClick={openNew} className="gap-2 bg-primary hover:bg-primary/90"><Plus size={18} /> {t("add")}</Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {shown.map((c) => {
          const done = (c.checklist || []).filter((x) => x.done).length;
          return (
            <Card key={c.id} data-testid={`cleaning-card-${c.id}`} className="border-slate-200 p-5 transition-all hover:-translate-y-1 hover:shadow-md">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2"><Sparkles size={18} className="text-cyan-600" /><h3 className="font-head font-semibold text-slate-900">{aptName(c.apartment_id)}</h3></div>
                <StatusBadge value={c.status} />
              </div>
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
                <span>{fmtDate(c.date)}</span>
                <span className="flex items-center gap-1"><Clock size={12} /> {c.time}</span>
                <span className="flex items-center gap-1"><User size={12} /> {c.staff || "—"}</span>
              </div>
              {(c.checklist || []).length > 0 && <div className="mt-3 text-xs text-slate-600">{t("checklist")}: {done}/{c.checklist.length}</div>}
              {c.status === "completata" && c.completed_at && (
                <div className="mt-2 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700" data-testid={`cleaning-completed-${c.id}`}>
                  {t("completedAt")} {fmtDate(c.completed_at)}{c.completed_by ? ` · ${t("completedBy")}: ${c.completed_by}` : ""}
                </div>
              )}
              <div className="mt-4 flex gap-2">
                <Button data-testid={`edit-cleaning-${c.id}`} variant="outline" size="sm" className="flex-1 gap-1" onClick={() => openEdit(c)}><Pencil size={14} /> {t("edit")}</Button>
                <Button data-testid={`delete-cleaning-${c.id}`} variant="outline" size="sm" className="text-rose-600" onClick={() => del(c.id)}><Trash2 size={14} /></Button>
              </div>
            </Card>
          );
        })}
        {shown.length === 0 && <p className="text-sm text-slate-400">{t("noData")}</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{form.id ? t("edit") : t("add")} · {t("cleanings")}</DialogTitle><DialogDescription className="sr-only">Form</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("apartment")} className="col-span-2">
              <Select value={form.apartment_id} onValueChange={(v) => set("apartment_id", v)}>
                <SelectTrigger data-testid="cleaning-apartment"><SelectValue placeholder={t("selectApartment")} /></SelectTrigger>
                <SelectContent>{apts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("date")}><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></Field>
            <Field label={t("time")}><Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} /></Field>
            <Field label={t("staff")}><Input value={form.staff} onChange={(e) => set("staff", e.target.value)} /></Field>
            <Field label={t("status")}>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="cleaning-status"><SelectValue /></SelectTrigger>
                <SelectContent>{CLEAN_STATUS.map((s) => <SelectItem key={s} value={s}>{t({ da_fare: "todo", in_corso: "inProgress", completata: "completed" }[s])}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="col-span-2">
              <Label className="mb-1.5 block text-xs">{t("checklist")}</Label>
              <div className="space-y-2">
                {form.checklist.map((task, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
                    <Checkbox checked={task.done} onCheckedChange={() => toggleTask(i)} />
                    <span className={`flex-1 text-sm ${task.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{task.label}</span>
                    <button type="button" onClick={() => removeTask(i)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input value={newTask} onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTask())} placeholder="+ task" data-testid="cleaning-newtask" />
                  <Button type="button" variant="outline" onClick={addTask}>+</Button>
                </div>
              </div>
            </div>
            <div className="col-span-1"><ImageUpload label={t("photoBefore")} value={form.photo_before} onChange={(u) => set("photo_before", u)} testid="cleaning-photo-before" /></div>
            <div className="col-span-1"><ImageUpload label={t("photoAfter")} value={form.photo_after} onChange={(u) => set("photo_after", u)} testid="cleaning-photo-after" /></div>
            <Field label={t("notes")} className="col-span-2"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>

            <div className="col-span-2 border-t border-slate-200 pt-3">
              <div className="mb-2 flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase text-slate-500">Servizi extra a pagamento</Label>
                <Select value="" onValueChange={(v) => v && addExtra(v)}>
                  <SelectTrigger className="w-56" data-testid="cleaning-add-extra"><SelectValue placeholder="+ Aggiungi servizio extra" /></SelectTrigger>
                  <SelectContent>{EXTRA_SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-3">
                {(form.extra_services || []).map((s, i) => (
                  <div key={s.id || i} data-testid={`extra-service-${i}`} className="rounded-lg border border-slate-200 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">{s.label}</span>
                      <button type="button" onClick={() => removeExtra(i)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <Field label="Prezzo (€)"><Input data-testid={`extra-price-${i}`} type="number" step="0.01" value={s.price} onChange={(e) => updateExtra(i, "price", +e.target.value)} /></Field>
                      <Field label="Stato">
                        <Select value={s.paid_status} onValueChange={(v) => updateExtra(i, "paid_status", v)}>
                          <SelectTrigger data-testid={`extra-paid-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="da_pagare">Da pagare</SelectItem><SelectItem value="pagato">Pagato</SelectItem></SelectContent>
                        </Select>
                      </Field>
                      <Field label="Provider">
                        <Select value={s.payment_provider || "none"} onValueChange={(v) => updateExtra(i, "payment_provider", v === "none" ? "" : v)}>
                          <SelectTrigger data-testid={`extra-provider-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="stripe">Stripe</SelectItem><SelectItem value="sumup">SumUp</SelectItem></SelectContent>
                        </Select>
                      </Field>
                      <Field label="Rif. prenotazione"><Input value={s.booking_ref || ""} onChange={(e) => updateExtra(i, "booking_ref", e.target.value)} placeholder="cod. OTA" /></Field>
                      <Field label="Link pagamento (predisposto)" className="col-span-2"><Input data-testid={`extra-link-${i}`} value={s.payment_link || ""} onChange={(e) => updateExtra(i, "payment_link", e.target.value)} placeholder="https://…" /></Field>
                      <Field label="Nota operativa" className="col-span-2"><Input value={s.notes || ""} onChange={(e) => updateExtra(i, "notes", e.target.value)} /></Field>
                    </div>
                  </div>
                ))}
                {(form.extra_services || []).length === 0 && <p className="text-xs text-slate-400">Nessun servizio extra. Se Stripe/SumUp non sono collegati, il servizio resta "Da pagare".</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-cleaning-btn" onClick={save} className="bg-primary hover:bg-primary/90">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
